import { useState, useEffect } from 'react';

interface JobDetail {
  job_summary: {
    job_id: string;
    city: string;
    service: string;
    date_time: string;
    duration: string;
  };
  pricing_breakdown: Array<{
    item: string;
    amount: number;
  }>;
  subtotal: number;
  commission: number;
  provider_payout: number;
}

interface Props {
  jobId: string | null;
  onClose: () => void;
}

export default function JobDetailDrawer({ jobId, onClose }: Props) {
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJobDetail();
    }
  }, [jobId]);

  const fetchJobDetail = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const token = localStorage.getItem('supabase_token');

      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-provider-earnings-job-detail/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setDetail(result);
      }
    } catch (error) {
      console.error('Error fetching job detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!jobId) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[500px] bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Job Details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {loading ? (
          <div className="p-6 space-y-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        ) : detail ? (
          <div className="p-6 space-y-6">
            {/* Job Summary */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Job summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Job ID</span>
                  <span className="text-gray-900 font-medium">{detail.job_summary.job_id.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">City</span>
                  <span className="text-gray-900 font-medium">{detail.job_summary.city}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Service</span>
                  <span className="text-gray-900 font-medium capitalize">{detail.job_summary.service}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Date &amp; time</span>
                  <span className="text-gray-900 font-medium">{formatDateTime(detail.job_summary.date_time)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Duration</span>
                  <span className="text-gray-900 font-medium">{detail.job_summary.duration}</span>
                </div>
              </div>
            </div>

            {/* Pricing Breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Pricing breakdown</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {detail.pricing_breakdown.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-700">{item.item}</span>
                    <span className="text-gray-900 font-medium">
                      {item.amount === 0 ? '—' : `£${item.amount.toFixed(2)}`}
                    </span>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-gray-900">Subtotal</span>
                    <span className="text-gray-900">£{detail.subtotal.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Taskbudy fee (15%)</span>
                  <span className="text-red-600 font-medium">−£{detail.commission.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-base font-semibold text-gray-900">You earned</span>
                    <span className="text-xl font-bold text-teal-600">£{detail.provider_payout.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <i className="ri-information-line text-blue-600 text-lg flex-shrink-0"></i>
                <p className="text-sm text-blue-900">
                  Platform fee covers payment processing, customer support, scheduling, and job protection.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>Unable to load job details</p>
          </div>
        )}
      </div>
    </>
  );
}
